import { Button, Modal, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  adminDeleteUserMutation,
  adminListUsersQueryKey,
  adminUpdateUserMutation,
  errorMessage,
  type UserResponse,
} from "@/api";
import { Text } from "@/components/Text";

export type UserAction = "deactivate" | "activate" | "delete";

const displayName = (user: UserResponse) => user.name || user.email;

/**
 * Role changes (immediate) and the confirm-first actions (deactivate,
 * activate, delete) for the Users table. Returns triggers plus the confirm
 * `dialog` to render once.
 */
export function useUserActions() {
  const { t } = useTranslation("admin");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ user: UserResponse; action: UserAction } | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: adminListUsersQueryKey() });
  const failureText = (error: unknown, fallback: string) =>
    error instanceof ApiError && error.code === "SEAT_LIMIT_REACHED"
      ? t("users.errors.SEAT_LIMIT_REACHED")
      : errorMessage(error, fallback);

  const update = useMutation(adminUpdateUserMutation());
  const remove = useMutation(adminDeleteUserMutation());

  const changeRole = (user: UserResponse, role: string) =>
    update.mutate(
      { path: { user_uuid: user.uuid }, query: { role } },
      {
        onSuccess: () => {
          toast.success(t("users.roleChanged"));
          void refresh();
        },
        onError: (error) => toast.error(failureText(error, t("users.roleChangeFailed"))),
      },
    );

  const confirm = () => {
    if (!pending) return;
    const { user, action } = pending;
    const name = displayName(user);
    const done = {
      onSuccess: () => {
        toast.success(t(`users.${action}d`, { name }));
        setPending(null);
        void refresh();
      },
      onError: (error: unknown) => toast.error(failureText(error, t("users.actionFailed"))),
    };
    if (action === "delete") remove.mutate({ path: { user_uuid: user.uuid } }, done);
    else update.mutate({ path: { user_uuid: user.uuid }, query: { is_active: action === "activate" } }, done);
  };

  const target = pending?.user;
  const action = pending?.action;
  const dialog = (
    <Modal
      open={pending !== null}
      onClose={() => setPending(null)}
      title={target && action ? t(`users.confirm.${action}Title`, { name: displayName(target) }) : undefined}
      actions={
        <>
          <Button variant="secondary" onClick={() => setPending(null)}>
            {t("users.confirm.cancel")}
          </Button>
          <Button
            variant={action === "activate" ? "primary" : "danger"}
            loading={update.isPending || remove.isPending}
            onClick={confirm}
          >
            {action ? t(`users.confirm.${action}Action`) : null}
          </Button>
        </>
      }
    >
      {target && action && (
        <div className="flex flex-col gap-2">
          <Text variant="body-sm" tone="secondary">
            {t(`users.confirm.${action}Body`, { email: target.email })}
          </Text>
          {action === "activate" && target.deactivationReason && (
            <Text variant="body-sm" tone="secondary">
              {t("users.confirm.previousReason", { reason: target.deactivationReason })}
            </Text>
          )}
        </div>
      )}
    </Modal>
  );

  return { changeRole, request: (user: UserResponse, a: UserAction) => setPending({ user, action: a }), dialog };
}
